import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

import { actionPlanApi } from 'areas/recommendations/api/actionPlanApi';
import { actionPlanTaskApi } from 'areas/recommendations/api/actionPlanTaskApi';

import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow, } from 'common/components/Table';
import { StyledCancelButton, StyledDeleteButton, StyledSaveButton, } from 'common/components/button/StyledButton';
import { PageSection } from 'common/styles/page';
import TableTabs from 'common/components/TableTabs';
import { useNavigate } from 'react-router';
import { Button, Icon, Modal, Popup } from 'semantic-ui-react';
import { PositionedModal } from 'common/components/PositionedModal';
import { ResourceListModal } from 'areas/settings/repository-management/components/ResourceListModal';
import { StatusIcon } from 'common/components/StatusIcon';
import { useToasts } from 'common/toast';
import TaskCompletionCircle from 'common/components/TaskCompletionCircle';
import {
  isCurrentUserStaffOrSuperAdmin,
  getOrganizationMembers,
  isCurrentMemberTeamLeader,
  isCurrentMemberOrgAdmin,
} from 'common/selectors/auth';
import { StyledDateInput } from 'common/components/DateInput';
import { Employee } from 'common/types/Employee';
import { AssessmentPeriodActionPlanTaskRead, AssessmentPeriodActionPlanTaskStatusEnum } from 'store/generated/portal/assessments/plans-tasks';

export const ASSESSMENT_PERIOD_ACTION_PLAN_TASK_STATUS = [
  { id: 1, label: 'Not Started' },
  { id: 2, label: 'In Progress' },
  { id: 3, label: 'Pending' },
  { id: 4, label: 'Deferred' },
  { id: 5, label: 'Completed' },
  { id: 6, label: 'Incomplete' },
  { id: 7, label: 'Not Applicable' },
] as const;

interface Task {
  id: number;
  phase_id: number;
  activity_name: string;
  task_name: string;
  metric: string;
  status: AssessmentPeriodActionPlanTaskStatusEnum;
  owner: number;
  start_date: string;
  end_date: string;
  notes: string;
  activity_sort_order: number;
  task_sort_order: number;
  content: {
    id: number;
    name: string;
    type: number;
    object_key: string;
  }[];
}

interface TeamActionPlanProps {
  taskData: any[];
  actionPlanId: number;
}

const TeamActionPlan: React.FC<TeamActionPlanProps> = ({ taskData, actionPlanId }) => {


  const [updateActionPlanTask] = actionPlanTaskApi.useApiPortalV1AssessmentsPlansTasksPartialUpdateMutation({});
  const [deleteActionPlan] = actionPlanApi.useApiPortalV1AssessmentsPlansOrganizationDestroyMutation({});

  const isUserAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isUserTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const isUserOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const canManagePlan = isUserAdmin || isUserOrgAdmin || isUserTeamLeader;

  const [completed, setCompleted] = useState([0, 0, 0]);
  const [isResourceListOpen, setResourceListOpen] = React.useState(false);
  const [resources, setResources] = useState<
    {
      id: number;
      name: string;
      type: number;
      object_key: string;
    }[]
  >([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const cancelClickedRef = React.useRef(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = useState<boolean>(true);

  const [selectedSubTab, setSelectedSubTab] = useState(0);
  const [editedTaskData, setEditedTaskData] = useState<
    Record<
      number,
      {
        owner?: number | null;
        status?: AssessmentPeriodActionPlanTaskStatusEnum;
        start_date?: string | null;
        end_date?: string | null;
        notes?: string | null;
        content?: {
          id: number;
          name: string;
          type: number;
        }[];
      }
    >
  >({});

  const members = useSelector(getOrganizationMembers);

  const toggleSection = (activityName: string) => {
    setOpenSections(prev => ({
      ...prev,
      [activityName]: !prev[activityName],
    }));
  };

  const toggleAllSections = () => {
    const newExpanded = !allExpanded;
    const newOpenSections = Object.keys(groupedData).reduce(
      (acc, key) => {
        acc[key] = newExpanded;
        return acc;
      },
      {} as Record<string, boolean>
    );
    setOpenSections(newOpenSections);
    setAllExpanded(newExpanded);
  };

  const getSectionCompletion = (tasks: Task[]): number => {
    const total = tasks.length;
    const completed = tasks.filter(task =>
      [5, 6, 7].includes(editedTaskData[task.id]?.status ?? task.status)
    ).length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  function useBlocker(
    blocker: () => boolean,
    when: boolean,
    onBlocked?: (args: any[], perform?: () => void) => void
  ) {
    const navigator = useContext(NavigationContext).navigator;

    useEffect(() => {
      if (!when) return;

      const push = navigator.push;

      navigator.push = (...args: [string, any?]) => {
        if (blocker()) {
          const perform = () => push(...args);
          if (onBlocked) {
            onBlocked(args, perform);
            return;
          }

          const confirm = window.confirm(
            'You have unsaved changes. Are you sure you want to leave?'
          );
          if (!confirm) return;
          perform();
          return;
        }
        push(...args);
      };

      return () => {
        navigator.push = push;
      };
    }, [blocker, when, navigator, onBlocked]);
  }

  // Modal-based blocker: show a styled confirmation instead of a native confirm
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const pendingPerformRef = React.useRef<(() => void) | null>(null);

  const handleBlocked = (args: any[], perform?: () => void) => {
    pendingPerformRef.current = perform ?? null;
    setUnsavedModalOpen(true);
  };

  useBlocker(
    () => !cancelClickedRef.current, // Only block if user didn't click cancel
    Object.keys(editedTaskData).length > 0, // Block only if there are unsaved changes
    handleBlocked
  );

  const handleChange = (taskId: number, field: string, value: any) => {
    setEditedTaskData(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
      },
    }));
  };

  const handleStatusIconClick = (taskId: number, currentStatus: number) => {
    const statusIds = ASSESSMENT_PERIOD_ACTION_PLAN_TASK_STATUS.map(s => s.id);
    const currentIndex = statusIds.indexOf(
      currentStatus as AssessmentPeriodActionPlanTaskStatusEnum
    );
    let nextIndex = currentIndex + 1;

    // if anything other than completed, set to completed, else set to not started
    if (currentIndex !== 4) {
      nextIndex = 4;
    } else {
      nextIndex = 0;
    }
    const nextStatus = statusIds[nextIndex];
    handleChange(taskId, 'status', nextStatus);
  };

  // Utility function to group tasks
  const groupByActivityName = (data: Task[], phase: number) =>
    data
      .filter(task => task.phase_id === phase + 1) // Filter tasks by the given phase
      .sort((a, b) => a.activity_sort_order - b.activity_sort_order) // Sort activity_sort_order
      .sort((a, b) => a.task_sort_order - b.task_sort_order) // Sort tasks by task_sort_order
      .reduce<Record<string, Task[]>>((acc, task) => {
        if (!acc[task.activity_name]) {
          acc[task.activity_name] = [];
        }
        acc[task.activity_name].push(task);
        return acc;
      }, {});

  const groupedData = useMemo(
    () => groupByActivityName(taskData, selectedSubTab),
    [taskData, selectedSubTab]
  );
  const allTasks = useMemo(() => Object.values(groupedData).flat(), [groupedData]);

  const computeCompletedPercent = (data: Task[], phase: number) => {
    const phaseTasks = data.filter(task => task.phase_id === phase);

    // Count the total tasks and tasks with status 5, 6, or 7
    const totalTasks = phaseTasks.length;
    const completedTasks = phaseTasks.filter(task => [5, 6, 7].includes(task.status)).length;

    // Compute the percentage of completed tasks
    const completedPercent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    return completedPercent;
  };

  useEffect(() => {
    setCompleted([
      computeCompletedPercent(taskData, 1),
      computeCompletedPercent(taskData, 2),
      computeCompletedPercent(taskData, 3),
    ]);
  }, [taskData]);

  // Initialize all sections as open when data loads
  useEffect(() => {
    setOpenSections(prev => {
      const newSections = { ...prev };
      Object.keys(groupedData).forEach(key => {
        if (!(key in newSections)) {
          newSections[key] = true;
        }
      });
      return newSections;
    });
  }, [groupedData]);

  // Save Project Plan
  const onSave = async () => {
    if (Object.keys(editedTaskData).length === 0) return; // No changes to save

    try {
      const updatePromises = Object.entries(editedTaskData).map(([taskId, updatedFields]) => {
        const { status, owner, start_date, end_date, notes } = updatedFields;
        const payload: Partial<AssessmentPeriodActionPlanTaskRead> = {};

        if (status !== undefined) {
          payload.status = status;
        }
        if (owner !== undefined) {
          payload.owner = owner;
        }
        if (start_date !== undefined) {
          payload.start_date = start_date ? new Date(start_date).toISOString().split('T')[0] : null;
        }
        if (end_date !== undefined) {
          payload.end_date = end_date ? new Date(end_date).toISOString().split('T')[0] : null;
        }

        if (notes !== undefined) {
          payload.notes = notes;
        }

        if (Object.keys(payload).length > 0) {
          return updateActionPlanTask({
            id: parseInt(taskId, 10),
            patchedAssessmentPeriodActionPlanTask: payload,
          }).unwrap();
        }

        return Promise.resolve(); // No-op for tasks with no changes
      });
      await Promise.all(updatePromises); // Wait for all API calls to complete
      dispatch(actionPlanTaskApi.util.resetApiState());
    } catch (error) {
      console.error('Error saving tasks:', error);
    } finally {
      setEditedTaskData({});
      addToast('Changes saved successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
    }
  };

  const handleViewClick = (
    resources: {
      id: number;
      name: string;
      type: number;
      object_key: string;
    }[]
  ) => {
    setResourceListOpen(true);
    setResources(resources);
  };

  const [activeTaskId, setActiveTaskId] = useState<number | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  const openNotesModal = (taskId: number) => {
    setActiveTaskId(taskId);
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setActiveTaskId(null);
  };

  return (
    <>
      <PageSection
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {/* Tabs Section */}
        <TableTabs
          options={[
            { label: 'Discover', percent: completed[0] },
            { label: 'Design', percent: completed[1] },
            { label: 'Deploy', percent: completed[2] },
          ]}
          onSelect={(item: number) => setSelectedSubTab(item)}
          selected={selectedSubTab}
        />

        <PageSection
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          gap="8px"
          alignItems="center"
        >
          <StyledSaveButton
            color={Object.keys(editedTaskData).length === 0 ? 'grey' : 'green'}
            label="Save Progress"
            width="140px"
            onClick={onSave}
          />
          <StyledCancelButton
            label="Cancel"
            width="90px"
            onClick={() => {
              cancelClickedRef.current = true;
              navigate(`/app/team-projects/`);
            }}
          />
        </PageSection>
      </PageSection>

      <Table>
        <TableHeader>
          <HeaderCell width={5}>Task</HeaderCell>
          <HeaderCell width={3}>Metric</HeaderCell>
          <HeaderCell width={2}>Status</HeaderCell>
          <HeaderCell width={2}>Owner</HeaderCell>
          <HeaderCell width={1}>Start</HeaderCell>
          <HeaderCell width={1}>End</HeaderCell>
          <HeaderCell width={2}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'end' }}
            >
              <Icon
                name={allExpanded ? 'minus square outline' : 'plus square outline'}
                style={{ cursor: 'pointer' }}
                onClick={toggleAllSections}
                size="large"
                color="grey"
              />
            </div>
          </HeaderCell>
        </TableHeader>
        <TableBody>
          {Object.entries(groupedData).map(([activityName, tasks]) => (
            <React.Fragment key={activityName}>
              <TableRow selectable="true" onClick={() => toggleSection(activityName)}>
                <TableCell colSpan={7} backgroundColor="#f3f3f3">
                  <DividerTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <TaskCompletionCircle percent={getSectionCompletion(tasks)} size={20} />
                      {activityName}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icon color="grey" name={openSections[activityName] ? 'minus' : 'plus'} />
                    </div>
                  </DividerTitle>
                </TableCell>
              </TableRow>

              {openSections[activityName] &&
                tasks.map(task => (
                  <TableRow key={task.id}>
                    <TableCell>
                      <StatusIcon
                        status={editedTaskData[task.id]?.status ?? task.status}
                        size="large"
                        onClick={() =>
                          handleStatusIconClick(
                            task.id,
                            editedTaskData[task.id]?.status ?? task.status
                          )
                        }
                      />{' '}
                      {task.task_name}
                    </TableCell>
                    <TableCell>{task.metric}</TableCell>
                    <TableCell>
                      <Select
                        value={editedTaskData[task.id]?.status || task.status}
                        onChange={e =>
                          handleChange(
                            task.id,
                            'status',
                            Number(e.target.value) as AssessmentPeriodActionPlanTaskStatusEnum
                          )
                        }
                      >
                        {ASSESSMENT_PERIOD_ACTION_PLAN_TASK_STATUS.map(status => (
                          <option key={status.label} value={status.id}>
                            {status.label}
                          </option>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={editedTaskData[task.id]?.owner ?? task.owner ?? ''}
                        onChange={e =>
                          handleChange(
                            task.id,
                            'owner',
                            e.target.value ? parseInt(e.target.value, 10) : null
                          )
                        }
                        disabled={!canManagePlan}
                      >
                        <option value="">* No Owner Assigned *</option>
                        {[...members].map((member: Employee) => (
                          <option key={member.id} value={member.id}>
                            {(member.firstName || member.lastName) ? `${member.lastName}${member.firstName ? `, ${member.firstName}` : ''}` : ''}
                          </option>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <StyledDateInput
                        value={
                          editedTaskData?.[task.id]?.start_date !== undefined
                            ? (editedTaskData[task.id]?.start_date ?? null)
                            : (task.start_date ?? null)
                        }
                        onChange={val => handleChange(task.id, 'start_date', val)}
                        hasValue={!!(editedTaskData?.[task.id]?.start_date || task.start_date)}
                      />
                    </TableCell>
                    <TableCell>
                      <StyledDateInput
                        value={
                          editedTaskData?.[task.id]?.end_date !== undefined
                            ? (editedTaskData[task.id]?.end_date ?? null)
                            : (task.end_date ?? null)
                        }
                        onChange={val => handleChange(task.id, 'end_date', val)}
                        hasValue={!!(editedTaskData?.[task.id]?.end_date || task.end_date)}
                      />
                    </TableCell>

                    <TableCell>
                      {(() => {
                        const note =
                          editedTaskData[task.id]?.notes !== undefined
                            ? editedTaskData[task.id]?.notes
                            : (task.notes ?? '');

                        return note ? (
                          <Popup
                            content={note}
                            position="top center"
                            trigger={
                              <Button
                                icon="edit"
                                style={{
                                  backgroundColor: 'rgba(129, 199, 132, 0.5)',
                                  border: '1px solid rgba(129, 199, 132, 1)', // Optional border for clarity
                                }}
                                onClick={() => openNotesModal(task.id)}
                              />
                            }
                          />
                        ) : (
                          <Button icon="edit" onClick={() => openNotesModal(task.id)} />
                        );
                      })()}

                      <Button
                        icon="television"
                        disabled={task.content.length === 0}
                        onClick={() => handleViewClick(task.content || {})}
                        type="button"
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      {isUserAdmin && (
        <PageSection
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          style={{ marginTop: '16px' }}
        >
          <StyledDeleteButton
            label="Delete Plan"
            width="120px"
            color="grey"
            onClick={async () => {
              const confirmed = window.confirm(
                `Are you sure you want to permanently delete this team project?`
              );

              if (!confirmed) return;

              try {
                await deleteActionPlan({ id: actionPlanId }).unwrap();
                addToast('Plan deleted successfully', {
                  appearance: 'success',
                  autoDismiss: true,
                });
              } catch (error) {
                console.error('Error deleting plan:', error);
              } finally {
                dispatch(actionPlanApi.util.resetApiState());
                navigate(`/app/team-projects/`);
              }
            }}
          />
        </PageSection>
      )}

      <ResourceListModal
        resources={resources || []}
        isOpen={isResourceListOpen}
        setModalOpen={setResourceListOpen}
      />

      {activeTaskId !== null && (
        <PositionedModal open={notesModalOpen} onClose={closeNotesModal} size="small" closeIcon>
          <Modal.Header>Task Notes</Modal.Header>
          <Modal.Content>
            <TextArea
              autoFocus
              style={{ width: '100%', height: '200px' }}
              value={
                editedTaskData[activeTaskId]?.notes !== undefined
                  ? (editedTaskData[activeTaskId]?.notes ?? '')
                  : (allTasks.find(t => t.id === activeTaskId)?.notes ?? '')
              }
              onChange={e => handleChange(activeTaskId, 'notes', e.target.value)}
            />
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={closeNotesModal} color="grey">
              Close
            </Button>
          </Modal.Actions>
        </PositionedModal>
      )}

      {/* Unsaved changes confirmation modal (replaces window.confirm) */}
      <PositionedModal open={unsavedModalOpen} onClose={() => setUnsavedModalOpen(false)} size="small">
        <Modal.Header>Unsaved changes</Modal.Header>
        <Modal.Content>
          You have unsaved changes. Are you sure you want to leave without saving?
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setUnsavedModalOpen(false)} color="grey">
            Stay
          </Button>
          <Button
            color="red"
            onClick={() => {
              setUnsavedModalOpen(false);
              try {
                pendingPerformRef.current && pendingPerformRef.current();
              } finally {
                pendingPerformRef.current = null;
              }
            }}
          >
            Leave
          </Button>
        </Modal.Actions>
      </PositionedModal>
    </>
  );
};

export default TeamActionPlan;

const Select = styled.select`
  width: 100%;
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const DividerTitle = styled.div`
  display: flex;
  flex-direction: row;
  text-align: space-between;
  justify-content: space-between;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 50px;
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;
